import React from 'react';

import { observer } from 'mobx-react';
import { Button, Container } from '@mui/material';
import { LbInput } from '@infoods/lifebase-ui';

import {
  InputBox,
  LbAutocomplete,
  PhoneNumberInput,
} from '../../../shared/components/form-components';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';
import {
  validateEmail,
  validateZipCode,
  validateExistEmail,
  validateWhitespace,
} from '../../../shared/utils/reactHookFormUtils';
import { validateCoachSeats } from './utils/validateCoachSeats';
import { states } from '../../../shared/constants/states';
import { useCoachSetup } from '../../hooks/useCoachSetup';
import { EntityBasic } from '../../interfaces/EntityBasic';
import classes from './NgzCoachSetup.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';

const NgzCoachSetup = observer(() => {
  const { isSuperAdmin } = loggedInUserDataStore;
  const { getForAgent } = useUserRoleLabel();

  const { form, onSubmit, entities } = useCoachSetup();
  const { control, handleSubmit } = form;

  const generalInputProps = {
    control,
    size: 'small',
    variant: 'standard',
  } as const;

  return (
    <Container>
      <h1
        className={classes.title}
      >{`Add a New  ${getForAgent({ case: 'capital' })}`}</h1>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <InputBox title="Personal Data">
          {isSuperAdmin && (
            <LbAutocomplete
              {...generalInputProps}
              label={`Add ${getForAgent({ case: 'capital' })} to Entity`}
              name="entity"
              getOptionKey={(option) => option.id}
              options={entities}
              getOptionLabel={(option) => option.business_name}
              isOptionEqualToValue={(option: EntityBasic, value: EntityBasic) =>
                option?.id === value?.id
              }
              rules={{
                required: 'Entity is required',
                validate: validateCoachSeats,
              }}
            />
          )}
          <LbInput
            {...generalInputProps}
            label="First Name"
            name="firstName"
            rules={{
              required: 'First name is required',
              validate: validateWhitespace,
            }}
          />
          <LbInput
            {...generalInputProps}
            label="Last Name"
            name="lastName"
            rules={{
              required: 'Last name is required',
              validate: validateWhitespace,
            }}
          />
          <LbInput
            {...generalInputProps}
            label="Email"
            name="email"
            type="email"
            rules={{
              required: 'Email is required',
              validate: {
                validateEmail,
                validateExistEmail,
              },
            }}
          />
          <PhoneNumberInput
            {...generalInputProps}
            label="Cell Phone"
            name="additional.cellPhone"
            rules={{ required: 'Cell phone is required' }}
            allowEmptyFormatting={false}
          />
          <LbInput {...generalInputProps} label="Facebook" name="additional.facebook" />
          <LbInput {...generalInputProps} label="Instagram" name="additional.instagram" />
        </InputBox>

        <InputBox title="Address">
          <LbInput
            {...generalInputProps}
            label="Address"
            name="additional.address1"
            rules={{
              required: 'Address is required',
              validate: validateWhitespace,
            }}
          />
          <LbInput
            {...generalInputProps}
            label="City"
            name="additional.city"
            rules={{ required: 'City is required', validate: validateWhitespace }}
          />
          <LbAutocomplete
            {...generalInputProps}
            label="State"
            name="additional.state"
            options={states}
            rules={{
              required: 'State is required',
              deps: ['additional.zip'],
            }}
          />
          <LbInput
            {...generalInputProps}
            label="Zip Code"
            name="additional.zip"
            rules={{
              required: 'Zip code is required',
              validate: async (zip, { additional }) =>
                validateZipCode(zip, additional.state),
            }}
          />
        </InputBox>

        <Button type="submit" className={classes.submitButton} variant="contained">
          {`Complete ${getForAgent({ case: 'capital' })} Setup`}
        </Button>
      </form>
    </Container>
  );
});

angularizeWithProviders(NgzCoachSetup, 'ngzCoachSetup', {});

export { NgzCoachSetup };
